import "./hub-page.scss";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { SyncButtonComponent } from "../../components/button-component";
import { Compartment, CompartmentStates } from "../../services/hub/compartment";
import { Group } from "../../services/hub/group";
import LockComponent from "../../components/lock-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useMyHub from "../../services/hub/useMyHub";
import useCompartmentSizes from "../../services/hub/useCompartmentSizes";
import Member from "../../services/member/member";
import Dropdown from "bootstrap/js/dist/dropdown";
import { CompartmentSize } from "../../services/hub/compartmentSize";
import HubCompartmentDetail from "./hub-compartment-detail";
import HubLegend from "./hub-details/hub-legend";
import HubOpenAllDoor from "./hub-details/hub-open-all-door";
import HubStateInformation from "./hub-details/hub-state-information";

interface IParamTypes {
  hubId: string;
}

type HubPageProps = {
  member: Member;
};

interface IDropdownSideProps {
  onChange: (side: Side) => void;
}

enum Side {
  Front = "front",
  Back = "back",
}

function DropdownSide(props: IDropdownSideProps) {
  const { onChange } = props;

  const [state, setState] = useState<Side>(Side.Front);
  const [dropdown, setDropdown] = useState<Dropdown | null>(null);
  const ddRef = useRef(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (!dropdown) {
      setDropdown(new Dropdown(ddRef.current || "", {}));
    }
  }, [dropdown]);

  const onDropdownClick = () => {
    dropdown?.show();
    //dropdown?.hide();
  };

  const onItemClick = (newSide: Side) => {
    if (state !== newSide) {
      setState(newSide);
      onChange(newSide);
    }

    dropdown?.hide();
  };

  return (
    <div className="dropdown ms-2">
      <button
        className="btn btn-outline-primary dropdown-toggle"
        type="button"
        data-bs-toggle="dropdown"
        ref={ddRef}
        aria-expanded="false"
        onClick={onDropdownClick}
      >
        {t(`hubs-page.dropdown-side-${state.toString()}`)}
      </button>
      <ul className="dropdown-menu" aria-labelledby="dropdown1">
        {Object.values(Side).map((state: Side) => {
          return (
            <li key={state}>
              <button
                className="dropdown-item"
                onClick={() => {
                  onItemClick(state);
                }}
              >
                {t(`hubs-page.dropdown-side-${state}`)}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function HubPage(props: HubPageProps) {
  const { member } = props;
  const { hubId } = useParams<IParamTypes>();
  const { t } = useTranslation();

  const { hub, refresh } = useMyHub({ hubId: hubId, onErrorNotificationMessage: t("hub-page.error-refresh") });
  const { compartmentSizes } = useCompartmentSizes({ onErrorNotificationMessage: t("hub-page.error-refresh") });

  const [condensed] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [selectedCompartmentNumber, setSelectedCompartmentNumber] = useState<string>("");
  const [selectedSide, setSelectedSide] = useState<string>("front");
  const [showOpenAllDoors, setShowOpenAllDoors] = useState<boolean>(false);

  const selectedReservation = hub?.reservations.find(
    (r) =>
      r.groupId === selectedGroupId &&
      r.compartmentNumber === selectedCompartmentNumber &&
      Date.parse(r.start) < Date.now() &&
      Date.parse(r.end) > Date.now()
  );

  useEffect(() => {
    const timeoutId = setInterval(async () => {
      await refresh();
    }, 5000);
    return () => clearInterval(timeoutId);
  }, [refresh]);

  return (
    <div>
      <div className="container page hub-page">
        <div className="page-breadcrumb">
          <ul className="breadcrumb">
            <li className="breadcrumb-item active">
              <Link className="" to="/hubs">
                {t("hub-page.hublink")}
              </Link>
            </li>
            <li className="breadcrumb-item active">{hub ? hub.name : hubId}</li>
          </ul>
        </div>

        <HubStateInformation member={member} />

        {/* Groups/Compartments */}
        {hub && hub.groups && hub.groups.length > 0 && (
          <div className="card">
            <div className="card-body">
              <h5 className="card-title toolbar">
                {t("hub-page.compartments")}
                {hub && (
                  <>
                    <SyncButtonComponent className="ms-2" onClick={refresh}></SyncButtonComponent>

                    <button className="ms-2 btn btn-outline-primary" onClick={() => setShowOpenAllDoors(true)}>
                      {t("hub-page.open-all-doors")}
                    </button>

                    {hub.flowConfigs.isRiderLocker && (
                      <DropdownSide
                        onChange={(side) => {
                          setSelectedSide(side);
                        }}
                      />
                    )}
                  </>
                )}
              </h5>

              <HubLegend />

              <div className="row g-1">
                {hub?.groups?.map((group) => {
                  return (
                    <div
                      key={group.id}
                      className={
                        "panel-group " +
                        (condensed ? "col-3 col-md-2 col-lg-1" : "col-4 col-md-3 col-lg-2") +
                        (group.state !== "Online" ? " disabled" : "")
                      }
                    >
                      <div className="row">
                        <div className="col-12">
                          <div className="group-title">{group.id} </div>
                        </div>
                      </div>

                      <div className="compartments">
                        {group.compartments.map((compartment) => {
                          const reservation = hub.reservations.find(
                            (r) =>
                              r.groupId === group.id &&
                              r.compartmentNumber === compartment.number &&
                              Date.parse(r.start) < Date.now() &&
                              Date.parse(r.end) > Date.now()
                          );
                          const isOpened = compartment.state === CompartmentStates.Opened;
                          const isDeactivated = hub?.reservationDenies?.some(
                            (r) => r.compartmentNumber === compartment.number && r.groupId === group.id && r.hubId === hub.id
                          );
                          const compartmentName = group.id + compartment.number;
                          var compartmentSize = compartmentSizes.find((cs) => cs.id === compartment.sizeId);
                          const compartmentHeight = (condensed ? 40 : compartmentSize ? compartmentSize.height * 7.3 : 50) + "px";

                          let kioskSize: CompartmentSize | undefined = undefined;
                          if (hub.kiosk && hub.kiosk.sizeId && hub.kiosk.position === compartmentName) {
                            kioskSize = compartmentSizes.find((cs) => cs.id === hub.kiosk?.sizeId);
                          }
                          const kioskHeight = (kioskSize ? kioskSize.height * 7.3 : 50) + "px";

                          return (
                            <div key={compartmentName}>
                              {/* Kiosk */}
                              {kioskSize && (
                                <div key={"kiosk" + compartmentName} style={{ height: kioskHeight }} className={"panel-kiosk"}>
                                  <div className="kiosk"></div>
                                  {/* <span className="item-number float-start">{compartment.number}</span> */}
                                </div>
                              )}

                              {/* Compartment */}
                              <div
                                style={{ height: compartmentHeight }}
                                className={
                                  "panel-compartment clickable" +
                                  (reservation ? " reservation" : "") +
                                  (isOpened ? " opened" : " closed") +
                                  (isDeactivated ? " disabled" : "")
                                }
                                onClick={() => {
                                  onCompartmentSelected(group, compartment);
                                }}
                              >
                                <div className="row">
                                  <div className="col-12 text-center">
                                    <span className="item-number float-start">{compartment.number}</span>

                                    <span className={"float-end item-lock" + (isOpened ? " opened" : " closed")}>
                                      {reservation?.paymentId && <FontAwesomeIcon className="me-2" icon={["fas", "credit-card"]} />}
                                      <LockComponent opened={isOpened} disabled={hub.state !== "Online" || group.state !== "Online"} />
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedGroupId !== "" && selectedCompartmentNumber !== "" && (
        <HubCompartmentDetail
          hub={hub}
          orderId={selectedReservation?.orderId}
          paymentId={selectedReservation?.paymentId}
          groupId={selectedGroupId}
          compartmentNumber={selectedCompartmentNumber}
          side={selectedSide}
          member={member}
          onClose={clearCompartmentSelected}
          refresh={refresh}
        />
      )}

      {showOpenAllDoors && <HubOpenAllDoor hub={hub} onClose={() => setShowOpenAllDoors(false)} refresh={refresh} />}
    </div>
  );

  function onCompartmentSelected(group: Group, compartment: Compartment) {
    setSelectedGroupId(group.id);
    setSelectedCompartmentNumber(compartment.number);
  }

  function clearCompartmentSelected() {
    setSelectedGroupId("");
    setSelectedCompartmentNumber("");
  }
}
